import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Select,
  FormErrorMessage,
  VStack,
  Box,
  Text,
  Image,
  useToast,
} from "@chakra-ui/react";
import axiosInstance from "../../helpers/axiosInstance";

/**
 * Modal for adding a custom Bucket List service item.
 * User can filter by preference, then select a service.
 * Maps: service._id → service_id, preference → type, and entire data object → item_data.
 * Shows a preview card of the selected service.
 */
export default function AddBucketListItemModal({
  isOpen,
  onClose,
  tripId,
  places,
  setPlaces,
  selectedDayIndex,
  city,
}) {
  const toast = useToast();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [selectedPreference, setSelectedPreference] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [itemData, setItemData] = useState(null);
  const [type, setType] = useState("");
  const [errors, setErrors] = useState({});

  // Fetch all services when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      (async () => {
        try {
          const res = await axiosInstance.get(
            `/bucket-list/filter-third-party-services?category=explore`
          );
          const raw = res.data.data?.services || [];
          setServices(raw);
          setFilteredServices(raw);
          // extract unique preference types
          const prefs = Array.from(new Set(raw.map((s) => s.preference)));
          setPreferences(prefs);
          // reset selection
          setSelectedPreference("");
          setServiceId("");
          setItemData(null);
          setType("");
        } catch (err) {
          console.error("Error fetching preferences:", err);
        }
      })();
    }
  }, [isOpen]);

  // Update filtered services when preference changes
  useEffect(() => {
    if (selectedPreference) {
      if (selectedPreference === "all") {
        setFilteredServices(services);
      } else {
        setFilteredServices(
          services.filter((s) => s.preference === selectedPreference)
        );
      }
      // clear previous service selection
      setServiceId("");
      setItemData(null);
      setType("");
    }
  }, [selectedPreference, services]);

  const validate = () => {
    const e = {};
    if (!selectedPreference) e.preference = "Preference is required";
    if (!serviceId) e.serviceId = "Service is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    // grab the real `day` from the selected itinerary slot
    const dayValue =
      places?.itinerary?.[selectedDayIndex]?.day ?? String(selectedDayIndex);

    const payload = {
      service_id: serviceId,
      item_data: itemData,
      day: dayValue,
      type,
      city,
    };

    try {
      const res = await axiosInstance.post(
        `/bucket-list/${tripId}/add-service-item`,
        payload
      );

      if (res.data.success) {
        const nextPlan = {
          ...places, // <- you already have `places` in scope if
          itinerary: places.itinerary.map(
            (
              day,
              idx //   you pass it down, or
            ) =>
              idx === selectedDayIndex //   compute from prevPlan
                ? {
                    ...day,
                    activity_detail_list: [
                      ...(day.activity_detail_list || []),
                      itemData,
                    ],
                  }
                : day
          ),
        };

        setPlaces(nextPlan);

        toast({
          title: "Activity added!",
          description: res.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Error adding bucket-list item:", err);
      toast({
        title: "Could not add activity.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxH="80vh" overflowY="auto">
        <ModalHeader>Add Activity from Bucket List</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isInvalid={!!errors.preference}>
              <FormLabel>Type</FormLabel>
              <Select
                placeholder="Select type"
                value={selectedPreference}
                onChange={(e) => setSelectedPreference(e.target.value)}
              >
                <option value="all">All</option>
                {preferences.map((pref) => (
                  <option key={pref} value={pref}>
                    {pref}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.preference}</FormErrorMessage>
            </FormControl>

            <FormControl
              isInvalid={!!errors.serviceId}
              isDisabled={!selectedPreference}
            >
              <FormLabel>Activities</FormLabel>
              <Select
                placeholder="Select activity"
                value={serviceId}
                onChange={(e) => {
                  const id = e.target.value;
                  setServiceId(id);
                  const selected = services.find((s) => s._id === id);
                  if (selected) {
                    setItemData(selected.data);
                    setType(selected.preference);
                  }
                }}
              >
                {filteredServices.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.data.title}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.serviceId}</FormErrorMessage>
            </FormControl>

            {/* Preview card for selected service */}
            {itemData && (
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={4}
                display="flex"
                alignItems="center"
                w="100%"
                mt={2}
              >
                <Box flex="1">
                  <Text fontSize="lg" fontWeight="bold" noOfLines={2}>
                    {itemData.title}
                  </Text>
                  {itemData.reviews?.combinedAverageRating != null && (
                    <Text fontSize="sm" mt={1}>
                      Rating:{" "}
                      {itemData.reviews.combinedAverageRating.toFixed(2)} ★
                    </Text>
                  )}
                </Box>
                {itemData.images?.[0]?.variants[2]?.url && (
                  <Image
                    src={itemData.images[0].variants[2].url}
                    boxSize="80px"
                    objectFit="cover"
                    borderRadius="md"
                    ml={4}
                    alt={itemData.title}
                  />
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost">
            Cancel
          </Button>
          <Button colorScheme="customGreen" onClick={handleSave}>
            Add Activity
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
